<template>
  <div class="fixed inset-0 z-50 overflow-hidden">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-show="selectedAdventurer !== null"
        class="absolute inset-0 bg-gray-500 bg-opacity-50 fade-in"
        @click="$emit('close')"
      ></div>
    </Transition>

    <!-- Slide-over panel -->
    <Transition name="slide">
      <div
        v-if="selectedAdventurer"
        class="absolute inset-y-0 right-0 max-w-[800px] w-full bg-white bg-opacity-50 shadow-xl slide-in p-1 pr-0 rounded-l-lg"
      >
        <div class="h-full flex flex-col bg-white rounded-l-md px-4 py-2">
          <!-- Loading state -->
          <div
            v-if="isLoadingFellowData"
            class="flex-1 flex items-center justify-center py-12"
          >
            <div class="text-center">
              <Loader2 class="animate-spin h-10 w-10 mx-auto mb-3 text-gray-400" />
              <p class="text-sm text-gray-500">Loading fellow details...</p>
            </div>
          </div>
          <!-- Error state -->
          <div
            v-else-if="fetchError"
            class="flex-1 flex items-center justify-center py-12 px-4"
          >
            <div class="text-center">
              <p class="text-sm text-red-600 mb-3">{{ fetchError }}</p>
              <button
                class="text-xs border border-gray-200 px-3 py-2 rounded-md hover:bg-gray-50"
                @click="fetchFellowData()"
              >
                Retry
              </button>
            </div>
          </div>
          <!-- Content (fetched fellow data) -->
          <template v-else-if="localAdventurer">
          <!-- Header -->
          <div class="py-3 px-2">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="localAdventurer.profile_details?.user_details?.avatar_image"
                  :src="localAdventurer.profile_details.user_details.avatar_image"
                  :alt="localAdventurer.profile_details.user_details.first_name"
                  class="h-10 w-10 rounded-full object-cover"
                />
                <div>
                  <h2 class="text-lg font-medium text-gray-900">
                    {{ localAdventurer.profile_details?.user_details?.first_name }}
                    {{ localAdventurer.profile_details?.user_details?.last_name }}
                  </h2>
                  <p class="text-xs text-gray-500">
                    {{ localAdventurer.profile_details?.user_details?.email }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <div class="flex gap-2">
                  <a
                    v-if="localAdventurer.profile_details?.linkedin_url"
                    :href="localAdventurer.profile_details.linkedin_url"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <img src="/linkedin_logo.svg" class="w-5 h-5" alt="LinkedIn" />
                  </a>
                  <a
                    v-if="localAdventurer.profile_details?.telegram_handle"
                    :href="`https://t.me/${localAdventurer.profile_details.telegram_handle}`"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <img src="/telegram_logo.svg" class="w-5 h-5" alt="Telegram" />
                  </a>
                  <a
                    v-if="localAdventurer.profile_details?.github_url"
                    :href="localAdventurer.profile_details.github_url"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <Github class="w-5 h-5 text-gray-700" />
                  </a>
                  <a
                    v-if="localAdventurer.profile_details?.leetcode_url"
                    :href="localAdventurer.profile_details.leetcode_url"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <img src="/leetcode_logo.svg" class="w-5 h-5" alt="LeetCode" />
                  </a>
                  <a
                    v-if="localAdventurer.profile_details?.instagram_handle"
                    :href="`https://instagram.com/${localAdventurer.profile_details.instagram_handle}`"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <Instagram class="w-5 h-5 text-gray-700" />
                  </a>
                </div>
                <div class="flex gap-2">
                  <RouterLink
                    :to="`/profile/${localAdventurer.profile_details?.user_details?.username}`"
                    target="_blank"
                    class="inline-flex items-center gap-1 px-3 py-1.5 border-[1px] border-gray-200 hover:bg-gray-50 hover:shadow-sm rounded-md text-xs text-gray-700 transition-colors"
                  >
                    <User class="w-4 h-4" />
                    View Profile
                  </RouterLink>
                  <button
                    class="p-1 rounded-md text-gray-400 hover:text-gray-500"
                    @click="$emit('close')"
                  >
                    <X class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto">
            <div class="bg-gray-50 rounded-lg border-[1px] border-gray-200">
              <!-- Fellow Info Section -->
              <div class="p-4 border-b">
                <h3 class="text-sm font-medium text-gray-900 mb-3">Fellow Information</h3>
                <div class="flex gap-4">
                  <div class="flex items-center gap-2">
                    <TentTree class="w-4 h-4 text-gray-500" />
                    <span :class="getExpeditionStatusClass(localAdventurer.batch)">{{
                      localAdventurer.batch
                    }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <Briefcase class="w-4 h-4 text-gray-500" />
                    <span
                      :class="
                        getContractTypeStatusClass(
                          contractTypeChoices[
                            getKeyByValue(contractTypeChoices, localAdventurer.contract_type)
                          ]
                        )
                      "
                    >
                      {{
                        contractTypeChoices[
                          getKeyByValue(contractTypeChoices, localAdventurer.contract_type)
                        ]
                      }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 text-xs">
                    <MapPin class="w-4 h-4 text-gray-500" />
                    <span class="text-gray-700 border border-gray-200 rounded-full px-2 py-1">
                      {{ localAdventurer.profile_details?.city_of_residence || 'N/A' }}
                      <span v-if="localAdventurer.profile_details?.country_of_residence_details">
                        ,
                        {{
                          localAdventurer.profile_details.country_of_residence_details.country_name
                        }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Status Section -->
              <div class="p-4">
                <div class="flex gap-4">
                  <div class="flex-1">
                    <label class="text-sm text-gray-600 mb-1 block">Program Status</label>
                    <Select
                      v-model="localAdventurer.program_status"
                      @update:model-value="handleProgramStatusChange"
                      :disabled="isUpdatingProgramStatus"
                    >
                      <SelectTrigger class="w-full bg-white">
                        <SelectValue>
                          <template
                            v-if="
                              localAdventurer.program_status &&
                              programStatusChoices[localAdventurer.program_status]
                            "
                          >
                            <div
                              :class="
                                getProgramStatusClass(
                                  programStatusChoices[localAdventurer.program_status]
                                )
                              "
                            >
                              {{ programStatusChoices[localAdventurer.program_status] }}
                              <Loader2
                                v-if="isUpdatingProgramStatus"
                                class="animate-spin h-4 w-4 inline ml-1"
                              />
                            </div>
                          </template>
                          <span v-else class="text-gray-400">Select status...</span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            v-for="(value, key) in programStatusChoices"
                            :key="key"
                            :value="key"
                          >
                            <div :class="getProgramStatusClass(value)">{{ value }}</div>
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div class="flex-1">
                    <label class="text-sm text-gray-600 mb-1 block">Relationship Status</label>
                    <Select
                      v-model="localAdventurer.relationship_status"
                      @update:model-value="handleRelationshipChange"
                      :disabled="isUpdatingRelationship"
                    >
                      <SelectTrigger class="w-full bg-white">
                        <SelectValue>
                          <template
                            v-if="
                              localAdventurer.relationship_status &&
                              relationshipChoices[localAdventurer.relationship_status]
                            "
                          >
                            <div
                              :class="
                                getRelationshipStatusClass(
                                  relationshipChoices[localAdventurer.relationship_status]
                                )
                              "
                            >
                              {{ relationshipChoices[localAdventurer.relationship_status] }}
                              <Loader2
                                v-if="isUpdatingRelationship"
                                class="animate-spin h-4 w-4 inline ml-1"
                              />
                            </div>
                          </template>
                          <span v-else class="text-gray-400">Select status...</span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            v-for="(value, key) in relationshipChoices"
                            :key="key"
                            :value="key"
                          >
                            <div :class="getRelationshipStatusClass(value)">{{ value }}</div>
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Strikes Section -->
            <div v-if="localAdventurer.fellow_reports?.length" class="bg-gray-50 rounded-lg border-[1px] border-gray-200 mb-4 mt-4">
              <div class="p-4">
                <h3 class="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Flag class="w-4 h-4 text-yellow-500" />
                  Fellow Reports
                </h3>
                <ReportsList 
                  :reports="localAdventurer.fellow_reports"
                  :show-actions="true"
                  @report-updated="handleReportUpdated"
                />
              </div>
            </div>

            <!-- Preferences Section -->
            <div class="p-4 border-b">
              <h3 class="text-sm font-medium text-gray-900 mb-4">Preferences</h3>

              <!-- Languages -->
              <div
                v-if="localAdventurer.profile_details?.preferred_communication_language"
                class="mb-4"
              >
                <h4 class="text-sm font-medium text-gray-500 flex items-center gap-1 mb-2">
                  <Globe class="w-4 h-4" />
                  Communication Languages
                </h4>
                <div class="space-y-2">
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-500">Preferred:</span>
                    <span class="text-xs bg-lime-100 rounded-full px-3 py-1 capitalize">
                      {{ localAdventurer.profile_details.preferred_communication_language }}
                    </span>
                  </div>
                  <div v-if="parsedAdditionalLanguages?.length" class="flex items-center gap-2">
                    <span class="text-xs text-gray-500">Additional:</span>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="lang in parsedAdditionalLanguages"
                        :key="lang"
                        class="text-xs bg-gray-100 rounded-full px-3 py-1 capitalize"
                      >
                        {{ lang }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Companies -->
              <div
                v-if="localAdventurer.profile_details?.preferred_companies_details?.length"
                class="mb-4"
              >
                <h4 class="text-sm font-medium text-gray-500 flex items-center gap-2 mb-2">
                  <Building2 class="w-4 h-4" />
                  Preferred Companies
                </h4>
                <div class="flex flex-wrap gap-3">
                  <span
                    v-for="company in localAdventurer.profile_details.preferred_companies_details"
                    :key="company.id"
                    class="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-xs"
                  >
                    <img
                      v-if="company.logo"
                      :src="company.logo"
                      :alt="company.name"
                      class="w-5 h-5 object-contain"
                    />
                    <span class="text-xs text-gray-700">{{ company.name }}</span>
                  </span>
                </div>
              </div>

              <!-- Countries -->
              <div v-if="localAdventurer.profile_details?.preferred_countries_details?.length">
                <h4 class="text-sm font-medium text-gray-500 flex items-center gap-2 mb-2">
                  <Globe2 class="w-4 h-4" />
                  Preferred Countries
                </h4>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="country in localAdventurer.profile_details.preferred_countries_details"
                    :key="country.id"
                    class="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md"
                  >
                    <span class="text-lg">{{ getCountryFlag(country.country_name) }}</span>
                    <span class="text-xs text-gray-700">{{ country.country_name }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments Section -->
            <InternalCommentsSection 
              :comments="localAdventurer.internal_comments || []" 
              :username="localAdventurer.profile_details?.user_details?.username"
              @comments-updated="handleCommentsUpdated"
            />
          </div>
          </template>
        </div>
      </div>
    </Transition>

    <!-- Strike Form -->
    <Dialog :open="showStrikeForm" @update:open="showStrikeForm = false">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Issue Warning</DialogTitle>
          <DialogDescription>
            Review and confirm warning issuance. This action will mark the report as approved.
          </DialogDescription>
        </DialogHeader>
        <div class="grid gap-4 py-4">
          <div class="grid gap-2">
            <Label for="severity">Severity</Label>
            <Select v-model="strikeForm.severity">
              <SelectTrigger>
                <SelectValue placeholder="Select severity level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MINOR">Minor</SelectItem>
                <SelectItem value="MODERATE">Moderate</SelectItem>
                <SelectItem value="SEVERE">Severe</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label for="review_notes">Review Notes (Optional)</Label>
            <Textarea
              id="review_notes"
              v-model="strikeForm.review_notes"
              placeholder="Add any additional notes about this warning"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="showStrikeForm = false">Cancel</Button>
          <Button 
            variant="destructive" 
            :disabled="!strikeForm.severity || isSubmitting"
            @click="submitStrike"
            class="w-fit flex items-center gap-2"
          >
            <Loader2 v-if="isSubmitting" class="h-4 w-4 animate-spin" />
            Issue Warning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Revoke Form -->
    <Dialog :open="showRevokeForm" @update:open="showRevokeForm = false">
      <DialogContent class="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle class="text-lg">Revoke Strike</DialogTitle>
        </DialogHeader>
        <div class="grid gap-4">
          <div class="grid gap-2">
            <Label for="revocation_reason" class="font-base">Reason for revoking warning (required)</Label>
            <Textarea
              id="revocation_reason"
              class="h-24"
              v-model="revokeForm.reason"
              placeholder="Explain why this warning is being revoked"
            />
          </div>
        </div>
        <DialogFooter class="flex justify-end gap-2">
          <button class="text-xs border-[1px] border-gray-200 px-3 py-2 rounded-md" @click="showRevokeForm = false">Cancel</button>
          <button 
            class="text-xs bg-red-500 text-white px-4 py-2 rounded-md w-fit flex items-center gap-2"
            :disabled="!revokeForm.reason || isSubmitting"
            @click="submitRevoke"
          >
            <Loader2 v-if="isSubmitting" class="h-4 w-4 animate-spin" />
            Revoke Strike
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  X,
  TentTree,
  Briefcase,
  Loader2,
  MapPin,
  Building2,
  Globe2,
  Github,
  Instagram,
  User,
  Globe,
  Flag
} from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  getProgramStatusClass,
  getRelationshipStatusClass,
  getExpeditionStatusClass,
  getContractTypeStatusClass
} from '@/utils/statusClasses'
import {
  contractTypeChoices,
  programStatusChoices,
  relationshipChoices
} from '@/const/fellowChoices'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { getCountryFlag } from '@/utils/countryFlags'
import { RouterLink } from 'vue-router'
import InternalCommentsSection from '@/components/shared/InternalCommentsSection.vue'
import ReportsList from '@/components/shared/ReportsList.vue'
import type { Report, FellowReport } from '@/types/reports'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import type { Adventurer } from '@/types/adventurer'
import { useProfileStore } from '@/stores/profile'

const profileStore = useProfileStore()

const props = defineProps<{
  selectedAdventurer: Adventurer | null
  isUpdatingProgramStatus: boolean
  isUpdatingRelationship: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'program-status-change', value: unknown): void
  (e: 'relationship-change', value: unknown): void
  (e: 'comments-updated', comments: any[]): void
  (e: 'report-action', action: string, report: Report | FellowReport): void
  (e: 'report-updated', report: Report | FellowReport): void
}>()

const showStrikeForm = ref(false)
const showRevokeForm = ref(false)
const isSubmitting = ref(false)

const expandedFellowData = ref<Adventurer | null>(null)
const isLoadingFellowData = ref(false)
const fetchError = ref<string | null>(null)

function normalizeFellowData(data: any): Adventurer {
  const normalized = { ...data } as Adventurer
  if (data.outtalent_program_status) {
    normalized.program_status = data.outtalent_program_status as keyof typeof programStatusChoices
  }
  if (data.outtalent_relationship) {
    normalized.relationship_status = data.outtalent_relationship as keyof typeof relationshipChoices
  }
  if (data.user?.avatar_image && normalized.profile_details?.user_details) {
    normalized.profile_details = {
      ...normalized.profile_details,
      user_details: {
        ...normalized.profile_details.user_details,
        avatar_image: data.user.avatar_image
      }
    }
  }
  return normalized
}

async function fetchFellowData() {
  const username = props.selectedAdventurer?.profile_details?.user_details?.username
  if (!username) return
  isLoadingFellowData.value = true
  fetchError.value = null
  try {
    const data = await profileStore.fetchFellowDataByUsername(username)
    expandedFellowData.value = normalizeFellowData(data)
  } catch {
    fetchError.value = profileStore.fellowDataError || 'Failed to load fellow details'
  } finally {
    isLoadingFellowData.value = false
  }
}

watch(
  () => props.selectedAdventurer,
  (adventurer) => {
    if (!adventurer) {
      expandedFellowData.value = null
      fetchError.value = null
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchFellowData()
  },
  { immediate: true }
)

const strikeForm = ref({
  severity: '',
  review_notes: ''
})

const revokeForm = ref({
  reason: ''
})

const currentReport = ref<Report | FellowReport | null>(null)

const getKeyByValue = <T extends Record<string, string>>(obj: T, value: string): keyof T => {
  let key = Object.keys(obj).find((k) => obj[k as keyof T] === value)
  if (!key) {
    key = Object.keys(obj).find((k) => obj[k as keyof T].toLowerCase() === value?.toLowerCase())
  }
  if (!key) {
    key = Object.keys(obj).find((k) => k === value)
  }
  if (!key) {
    console.warn(`No matching key found for value: ${value}. Using value as fallback.`)
    return value as keyof T
  }
  return key as keyof T
}

const handleProgramStatusChange = (value: unknown) => {
  emit('program-status-change', value)
}

const handleRelationshipChange = (value: unknown) => {
  emit('relationship-change', value)
}

const handleCommentsUpdated = async () => {
  const username = localAdventurer.value?.profile_details?.user_details?.username
  if (!username) return
  try {
    const data = await profileStore.fetchFellowDataByUsername(username)
    expandedFellowData.value = normalizeFellowData(data)
    emit('comments-updated', data.internal_comments ?? [])
  } catch (err) {
    console.error('Failed to refresh comments:', err)
  }
}

const submitStrike = async () => {
  if (!currentReport.value || !strikeForm.value.severity) return
  
  isSubmitting.value = true
  try {
    const response = await api.patch(
      API_ROUTES.REVIEW_FELLOW_REPORT(currentReport.value.id.toString()),
      {
        status: 'APPROVED',
        severity: strikeForm.value.severity,
        review_notes: strikeForm.value.review_notes
      }
    )
    const updatedReport = response.data
    emit('report-updated', updatedReport)
    showStrikeForm.value = false
    strikeForm.value = { severity: '', review_notes: '' }
    currentReport.value = null
  } catch (error) {
    console.error('Error issuing warning:', error)
  } finally {
    isSubmitting.value = false
  }
}

const submitRevoke = async () => {
  if (!currentReport.value || !revokeForm.value.reason) return
  
  isSubmitting.value = true
  try {
    const response = await api.post(
      API_ROUTES.REVOKE_FELLOW_REPORT(currentReport.value.id.toString()),
      {
        reason: revokeForm.value.reason
      }
    )
    const updatedReport = response.data
    emit('report-updated', updatedReport)
    showRevokeForm.value = false
    revokeForm.value = { reason: '' }
    currentReport.value = null
  } catch (error) {
    console.error('Error revoking warning:', error)
  } finally {
    isSubmitting.value = false
  }
}

const handleReportUpdated = (updatedReport: Report | FellowReport) => {
  if (!localAdventurer.value?.fellow_reports) return

  // Update the report in the local array
  const index = localAdventurer.value.fellow_reports.findIndex(r => r.id === updatedReport.id)
  if (index !== -1) {
    localAdventurer.value.fellow_reports[index] = updatedReport as FellowReport
  }
}


const parsedAdditionalLanguages = computed(() => {
  // `additional_languages` is typed as `string[]` in `Adventurer`, but in practice
  // the backend may send different shapes (string / JSON string). Treat as unknown.
  const raw: unknown = (localAdventurer.value?.profile_details as any)?.additional_languages
  if (!raw) return []

  // API may return this as an array, a comma-separated string, or a JSON-encoded array string.
  if (Array.isArray(raw)) {
    return raw
      .filter((lang) => typeof lang === 'string' && lang.trim().length > 0)
      .map((lang) => lang.toLowerCase())
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim()

    if (!trimmed) return []

    // Try JSON array string first: '["English","French"]'
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed
            .filter((lang) => typeof lang === 'string' && lang.trim().length > 0)
            .map((lang) => lang.toLowerCase())
        }
      } catch {
        // fall through to comma-splitting
      }
    }

    // Fallback: comma-separated list: "English, French"
    return trimmed
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean)
      .map((lang: string) => lang.toLowerCase())
  }

  return []
})

// Fetched fellow data (loaded when panel opens via GET /profiles/fellow-data/by-username/{username}/)
const localAdventurer = computed(() => expandedFellowData.value)

</script>

<style>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for panel */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}

/* Fade in animation */
.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slide in animation */
.slide-in {
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
</style>
